import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { awaitJSON, JavaLambdaFetch, MiscLambdaFetch } from '@src/utils/fetch-utils';
import { CheckAuthParams } from './data-connector-tool-utils';
import {
  DataConnectorRow,
  DataConnectorRowParams,
  DataConnectorSourceType
} from './data-connector-tool-constants';

const ROWS_QUERY_KEY = ['dataConnectorRows'];

interface RawDataConnectorRow extends Omit<DataConnectorRow, 'enabled'> {
  enabled?: boolean | string;
}

const fetchDataConnectorRows = async (): Promise<DataConnectorRow[]> => {
  const res = await MiscLambdaFetch('/getMobiusDataConnectorConfig');
  const rows = await awaitJSON<RawDataConnectorRow[]>(res);
  // enabled can come back as a real boolean or the string "true"/"false" -- Boolean(row.enabled)
  // would treat the non-empty string "false" as truthy and invert a disabled row's state.
  return rows.map(row => ({ ...row, enabled: row.enabled === true || row.enabled === 'true' }));
};

export const useDataConnectorRows = (): ReturnType<typeof useQuery<DataConnectorRow[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ROWS_QUERY_KEY,
    queryFn: fetchDataConnectorRows
  });

export const checkDataConnectorAuth = async ({
  company,
  source,
  account_id: accountId,
  secret_path: secretPath,
  mcc_id: mccId
}: CheckAuthParams): Promise<boolean> => {
  try {
    // skipAuthRecovery: this endpoint validates a THIRD-PARTY connector's own credentials (e.g.
    // "is this Facebook Ads account still authorized") and proxies that check's raw status back —
    // a 401 here means the connector's external credentials are invalid, not that the calling
    // user's own session is dead. Without this flag, BpmLambdaFetch's recoverable-auth-error
    // handling (DEV-24818) would misread it as a session failure and try to refresh the caller's
    // Cognito token before retrying, for a status that has nothing to do with their session.
    const res = await JavaLambdaFetch('/data_connector_auth_check', {
      method: 'POST',
      body: { company, source, accountNumber: accountId, secretPath, mccId },
      skipAuthRecovery: true
    });
    return res.status === 200;
  } catch {
    return false;
  }
};

interface DataConnectorAccountLookupResult {
  empty: boolean;
  rows: {
    company: string;
    source: string;
    accountNumber: string;
    secretPath: string;
    brand: string;
    currencyCode: string;
  }[];
}

// Looks up the account name/currency for a newly-added row via its ad platform, so the row
// doesn't sit blank in the Account Name/Currency Code columns until the next full refetch. Only
// run over rows actually being added -- existing rows' names don't change.
const lookupDataConnectorAccounts = async (
  rows: DataConnectorRow[]
): Promise<DataConnectorRow[]> => {
  const res = await JavaLambdaFetch('/get_data_connector_account', {
    method: 'POST',
    body: rows.map(row => ({
      company: row.company,
      source: row.source,
      accountNumber: row.account_id,
      secretPath: row.secret_path,
      mccId: row.mcc_id
    }))
  });
  const { empty, rows: lookedUpRows } = await awaitJSON<DataConnectorAccountLookupResult>(res);
  if (empty) return rows;

  return rows.map(row => {
    const match = lookedUpRows.find(
      lookedUpRow =>
        lookedUpRow.company === row.company &&
        lookedUpRow.source === row.source &&
        lookedUpRow.accountNumber === row.account_id &&
        lookedUpRow.secretPath === row.secret_path
    );
    return match ? { ...row, account_name: match.brand, currency_code: match.currencyCode } : row;
  });
};

const saveDataConnectorRows = async (rowsToInsert: DataConnectorRow[]) => {
  const enrichedInserts =
    rowsToInsert.length > 0 ? await lookupDataConnectorAccounts(rowsToInsert) : rowsToInsert;
  await MiscLambdaFetch<DataConnectorRowParams>('/putMobiusDataConnectorJob', {
    method: 'POST',
    body: { insert: enrichedInserts }
  });
};

export const useSaveDataConnectorRows = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveDataConnectorRows,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROWS_QUERY_KEY })
  });
};

interface ToggleDataConnectorRowParams {
  accountId: string;
  source: DataConnectorSourceType;
  enabled: boolean;
}

const toggleDataConnectorRow = async ({
  accountId,
  source,
  enabled
}: ToggleDataConnectorRowParams) => {
  await MiscLambdaFetch('/toggleDataConnectorRow', {
    method: 'POST',
    body: { accountId, source, enabled: enabled ? 'true' : 'false' }
  });
};

export const useToggleDataConnectorRow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: toggleDataConnectorRow,
    onMutate: async ({ accountId, source, enabled }: ToggleDataConnectorRowParams) => {
      // Cancel any in-flight refetch first, or its response could land after this optimistic
      // write and overwrite it with stale (pre-toggle) data.
      await queryClient.cancelQueries({ queryKey: ROWS_QUERY_KEY });
      const previousRow = queryClient
        .getQueryData<DataConnectorRow[]>(ROWS_QUERY_KEY)
        ?.find(row => row.account_id === accountId && row.source === source);
      queryClient.setQueryData<DataConnectorRow[]>(ROWS_QUERY_KEY, rows =>
        rows?.map(row =>
          row.account_id === accountId && row.source === source ? { ...row, enabled } : row
        )
      );
      return { previousRow };
    },
    // Patches just the toggled row back rather than restoring a whole-cache snapshot, so a
    // failure on this row can't clobber a different row's still-in-flight or already-succeeded
    // optimistic update from an overlapping toggle.
    onError: (_error, { accountId, source }, context) => {
      if (context?.previousRow) {
        const { previousRow } = context;
        queryClient.setQueryData<DataConnectorRow[]>(ROWS_QUERY_KEY, rows =>
          rows?.map(row =>
            row.account_id === accountId && row.source === source ? previousRow : row
          )
        );
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ROWS_QUERY_KEY })
  });
};
